/** @format */

import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CrossIcon } from 'src/components/icons';
import type { ContentEditorPart, PatchContentPartFn } from 'src/types';

function splitList(value: unknown): string[] {
  if (typeof value !== 'string' || !value.trim()) return [];
  return value
    .split(/[,;\n]/)
    .map(s => s.trim())
    .filter(Boolean);
}

function normalizeList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(item => String(item).trim()).filter(Boolean) : [];
}

function appendUniqueValues(currentValues: string[], rawValue: string): string[] {
  const nextItems = splitList(rawValue);
  if (!nextItems.length) return currentValues;

  const existing = new Set(currentValues.map(item => item.toLowerCase()));
  const uniqueItems = nextItems.filter(item => {
    const normalizedItem = item.toLowerCase();
    if (existing.has(normalizedItem)) return false;

    existing.add(normalizedItem);
    return true;
  });

  return uniqueItems.length ? [...currentValues, ...uniqueItems] : currentValues;
}

interface CasaNumberTagInputProps {
  id: string;
  label: string;
  values?: string[];
  placeholder: string;
  onChange: (values: string[]) => void;
}

function CasaNumberTagInput({ id, label, values = [], placeholder, onChange }: CasaNumberTagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const normalizedValues = useMemo(() => normalizeList(values), [values]);

  const addInputValue = useCallback(() => {
    const nextValues = appendUniqueValues(normalizedValues, inputValue);
    setInputValue('');

    if (nextValues !== normalizedValues) {
      onChange(nextValues);
    }
  }, [inputValue, normalizedValues, onChange]);

  const removeValue = useCallback(
    (valueToRemove: string) => {
      onChange(normalizedValues.filter(value => value !== valueToRemove));
    },
    [normalizedValues, onChange]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' && event.key !== 'NumpadEnter') return;

    event.preventDefault();
    addInputValue();
  };

  return (
    <div className="content-editor__casa-row">
      <label className="content-editor__casa-label" htmlFor={id}>
        {label}
      </label>
      <div className="content-editor__casa-control">
        <input
          id={id}
          type="text"
          className="content-editor__casa-input"
          value={inputValue}
          onChange={event => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
        {normalizedValues.length > 0 ? (
          <div className="content-editor__casa-tags">
            {normalizedValues.map(value => (
              <span key={value} className="content-editor__casa-tag">
                <span>{value}</span>
                <button
                  type="button"
                  className="content-editor__casa-tag-remove"
                  onClick={() => removeValue(value)}
                  aria-label={`Remove ${value}`}>
                  <CrossIcon />
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface ContentEditorBlockBodyCasaNumberProps {
  part: ContentEditorPart;
  onPatch: PatchContentPartFn;
}

export default function ContentEditorBlockBodyCasaNumber({ part, onPatch }: ContentEditorBlockBodyCasaNumberProps) {
  const { t } = useTranslation();
  const onPartNumbersChange = useCallback(
    (nextValues: string[]) => {
      onPatch(part.id, { casaPartNumbers: nextValues });
    },
    [onPatch, part.id]
  );

  const onHierarchiesChange = useCallback(
    (nextValues: string[]) => {
      onPatch(part.id, { casaHierarchies: nextValues });
    },
    [onPatch, part.id]
  );

  const casaPart = part.type === 'casaNumber' ? part : null;

  return (
    <div className="content-editor__casa-fields">
      <CasaNumberTagInput
        id={`content-editor-casa-part-numbers-${part.id}`}
        label={t('contentEditor.casaPartNumbersLabel')}
        values={casaPart?.casaPartNumbers}
        placeholder={t('contentEditor.casaNumberInputPlaceholder')}
        onChange={onPartNumbersChange}
      />
      <CasaNumberTagInput
        id={`content-editor-casa-hierarchies-${part.id}`}
        label={t('contentEditor.casaHierarchiesLabel')}
        values={casaPart?.casaHierarchies}
        placeholder={t('contentEditor.casaNumberInputPlaceholder')}
        onChange={onHierarchiesChange}
      />
    </div>
  );
}
